import React, { useMemo, useState } from "react";
import Container from "../ui/Container";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { BRAND } from "../../lib/config";
import { DEDICATED_DISCOUNTS, DEDICATED_OFFERINGS } from "../../lib/dedicated";
import { Calendar, Tag, ArrowUpRight, MapPin, CheckCircle2 } from "lucide-react";

function moneyGBP(n, opts = {}) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: opts.max0 ? 0 : 2,
  }).format(n);
}

function CyclePill({ active, onClick, label, sublabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex-1 rounded-2xl border px-4 py-3 text-left transition",
        active
          ? "border-cyan-300/40 bg-cyan-500/10"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]",
      ].join(" ")}
    >
      <div className="text-sm font-semibold text-white">{label}</div>
      {sublabel ? <div className="mt-1 text-xs text-white/55">{sublabel}</div> : null}
    </button>
  );
}

function Spec({ k, v }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="text-sm text-white/65">{k}</div>
      <div className="text-sm font-semibold text-white">{v}</div>
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  const cls =
    tone === "cyan"
      ? "border-cyan-300/30 bg-cyan-500/10 text-white/85"
      : "border-white/10 bg-white/[0.02] text-white/70";
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${cls}`}>
      {children}
    </span>
  );
}

function LocationChip({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-xs font-semibold text-white/70">
      <MapPin className="h-3.5 w-3.5 text-cyan-300/90" />
      {children}
    </span>
  );
}

export default function DedicatedOfferings() {
  const [cycleId, setCycleId] = useState("m");
  const cycle = useMemo(
    () => DEDICATED_DISCOUNTS.find((c) => c.id === cycleId) || DEDICATED_DISCOUNTS[0],
    [cycleId]
  );

  const allLocations = useMemo(() => {
    const set = new Set();
    for (const o of DEDICATED_OFFERINGS) {
      (o.locations || []).forEach((l) => set.add(l));
    }
    return ["All locations", ...Array.from(set).sort()];
  }, []);

  const [locationFilter, setLocationFilter] = useState("All locations");

  const offerings = useMemo(() => {
    if (locationFilter === "All locations") return DEDICATED_OFFERINGS;
    return DEDICATED_OFFERINGS.filter((o) =>
      (o.locations || []).some((l) => l === locationFilter)
    );
  }, [locationFilter]);

  return (
    <section id="offerings" className="bg-[#070A0F] py-20 scroll-mt-20">
      <Container>
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="text-xs font-semibold tracking-wider text-white/50">DEDICATED OPTIONS</div>
          <h2 className="mt-3 text-balance text-4xl font-semibold leading-[0.95] tracking-tight text-white sm:text-5xl">
            Dedicated hardware.
            <span className="block">
              Clear pricing<span className="text-cyan-300">.</span>
            </span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-white/70 sm:text-lg">
            Choose a billing cycle and location. Stock may vary — we’ll confirm provisioning fast.
          </p>
        </div>

        {/* Controls (more cohesive) */}
        <div className="mt-10 mx-auto max-w-5xl rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="flex flex-col gap-6">
            {/* Top row: headings + filter */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Calendar className="h-4 w-4 text-cyan-300" />
                  Billing cycle
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {cycle.months > 1 ? (
                    <Pill tone="cyan">
                      <Tag className="h-4 w-4 text-cyan-300" />
                      Save {(cycle.discount * 100).toFixed(0)}% with {cycle.months} months
                    </Pill>
                  ) : (
                    <Pill>
                      <CheckCircle2 className="h-4 w-4 text-cyan-300" />
                      Pay as you go
                    </Pill>
                  )}
                  <Pill>Monthly is the anchor price</Pill>
                </div>
              </div>

              <div className="w-full lg:w-72">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
                  <MapPin className="h-4 w-4 text-cyan-300" />
                  Location
                </div>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-white outline-none focus:ring-2 focus:ring-cyan-300/50"
                >
                  {allLocations.map((l) => (
                    <option key={l} value={l} className="bg-[#070A0F]">
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pills */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {DEDICATED_DISCOUNTS.map((c) => (
                <CyclePill
                  key={c.id}
                  active={cycleId === c.id}
                  onClick={() => setCycleId(c.id)}
                  label={c.label}
                  sublabel={c.sublabel}
                />
              ))}
            </div>

            <p className="text-xs leading-5 text-white/55">
              Availability varies by stock and region. If you need a custom build, we’ll quote it.
            </p>
          </div>
        </div>

        {/* Offerings */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {offerings.map((o) => {
            const original = o.monthly * cycle.months;
            const dueToday = original * (1 - cycle.discount);
            const savings = original - dueToday;

            return (
              <Card key={o.id} className="p-7 sm:p-8">
                {/* Card header */}
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-lg font-semibold text-white leading-tight">
                      {o.name}
                      <span className="text-cyan-300">.</span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {o.badge ? <Pill>{o.badge}</Pill> : null}
                      {(o.locations || []).map((loc) => (
                        <LocationChip key={loc}>{loc}</LocationChip>
                      ))}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xs text-white/50">From</div>
                    <div className="mt-1 text-2xl font-semibold text-white">
                      {moneyGBP(o.monthly, { max0: true })}
                      <span className="text-white/40 text-base font-medium">/mo</span>
                    </div>
                  </div>
                </div>

                {/* Specs */}
                <div className="mt-6 space-y-3">
                  <Spec k="CPU" v={o.specs.cpu} />
                  <Spec k="RAM" v={o.specs.ram} />
                  <Spec k="Storage" v={o.specs.storage} />
                  <Spec k="Bandwidth" v={o.specs.bandwidth} />
                  <Spec k="DDoS" v={o.specs.ddos} />
                </div>

                {/* Total */}
                <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-xs font-semibold tracking-wider text-white/50">TOTAL</div>
                    {cycle.months > 1 ? (
                      <span className="text-xs font-semibold text-white/80">
                        Save {moneyGBP(savings)}
                      </span>
                    ) : (
                      <span className="text-xs text-white/45">Flexible</span>
                    )}
                  </div>

                  <div className="mt-2 text-sm text-white/70">
                    <span className="text-white/50">Due today:</span>{" "}
                    <span className="font-semibold text-white">
                      {cycle.months === 1
                        ? `${moneyGBP(o.monthly, { max0: true })}/mo`
                        : `${moneyGBP(dueToday, { max0: true })} for ${cycle.months} months`}
                    </span>
                    {cycle.months > 1 && (
                      <span className="ml-2 text-xs text-white/45">
                        (Was: {moneyGBP(original, { max0: true })})
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex flex-col gap-3">
                    <Button href={BRAND.supportUrl} className="w-full justify-center">
                      Request this server <ArrowUpRight className="h-4 w-4 opacity-80" />
                    </Button>
                    <Button variant="secondary" href={BRAND.discordUrl} className="w-full justify-center">
                      Ask in Discord
                    </Button>
                  </div>

                  <p className="mt-3 text-[11px] leading-5 text-white/50">
                    We’ll confirm provisioning time and stock after you request.
                  </p>
                </div>

                {/* Ideal for */}
                <div className="mt-6">
                  <div className="text-xs font-semibold tracking-wider text-white/50">IDEAL FOR</div>
                  <ul className="mt-3 space-y-2 text-sm text-white/65">
                    {o.idealFor.map((x) => (
                      <li key={x} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-cyan-300/80" />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>

        {offerings.length === 0 && (
          <div className="mt-10 mx-auto max-w-3xl text-center text-white/70">
            No offerings currently listed for <span className="text-white">{locationFilter}</span>.
            Try another location or contact support for a custom build.
          </div>
        )}
      </Container>
    </section>
  );
}
